import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import API from '@/apiRequest';
import FeatureSwitch from '@/components/common/FeatureSwitch';
import classes from '@/components/ReportOptions/style.module.scss';
import ClickAwayListener from '@/components/ui-bridge/core/ClickAwayListener';
import Grow from '@/components/ui-bridge/core/Grow';
import Paper from '@/components/ui-bridge/core/Paper';
import Popper from '@/components/ui-bridge/core/Popper';
import { useAsyncService } from '@/contexts/AsyncServiceContext';
import { useUser } from '@/contexts/UserContext';
import ReportItem from '@/svg-icons/report-item';
import ReportItemDownload from '@/svg-icons/report-item-download';
import ReportItemDownloadAll from '@/svg-icons/report-item-download-all';
import ReportPhotos from '@/svg-icons/report-photos';
import ReportPrint from '@/svg-icons/report-print';

const AUTO_TRANSLATE_SUPPORTED_COUNTRIES = new Set(['PT', 'CO']);
const REPORT_VERSION = 2;

const DIVERGENCE_PARTIES = [
  {
    party: 'TENANT',
    flag: 'has_finished_tenant_divergence',
    labelId: 'DIVERGENCE_REPORT_TENANT',
    defaultMessage: 'Relatório de contestação (Inquilino)',
  },
  {
    party: 'OWNER',
    flag: 'has_finished_owner_divergence',
    labelId: 'DIVERGENCE_REPORT_OWNER',
    defaultMessage: 'Relatório de contestação (Proprietário)',
  },
  {
    party: 'REAL_ESTATE',
    flag: 'has_finished_real_estate_divergence',
    labelId: 'DIVERGENCE_REPORT_REAL_ESTATE',
    defaultMessage: 'Relatório de contestação (Imobiliária)',
  },
  {
    party: 'OTHER',
    flag: 'has_finished_other_divergence',
    labelId: 'DIVERGENCE_REPORT_OTHER',
    defaultMessage: 'Relatório de contestação (Outros)',
  },
];

const ReportOptions = ({ anchorEl, closeHandler, entityId, intl, options, show, flags }) => {
  const { showReportLoadingToast } = useAsyncService();
  const user = useUser();

  const me = useMemo(() => {
    if (user && Object.keys(user).length > 0) return user;
    try {
      return JSON.parse(sessionStorage.getItem('me')) || {};
    } catch {
      return {};
    }
  }, [user]);

  const country = me?.profile?.country || 'BR';
  const isAutoTranslateSupported = AUTO_TRANSLATE_SUPPORTED_COUNTRIES.has(country);

  const [autoTranslate, setAutoTranslate] = useState(false);

  useEffect(() => {
    if (show) {
      setAutoTranslate(false);
    }
  }, [show]);

  const isInspection = useMemo(
    () => !Number.isNaN(parseFloat(entityId)) && Number.isFinite(Number(entityId)),
    [entityId]
  );
  const entityType = isInspection ? 'inspections' : 'orders';

  const handleOptionSelected = useCallback(
    async (reportOptions, picture, version = REPORT_VERSION) => {
      const payload = {
        ...reportOptions,
        withPictures: picture,
        version,
        ...(autoTranslate && isAutoTranslateSupported ? { auto_translate: 'en-US' } : {}),
      };

      try {
        closeHandler();
        const { data } = await API.post(`${entityType}/${entityId}/report`, payload);

        if (data?.flag === 'cache') return;

        showReportLoadingToast({
          label: payload?.label || '',
          requestId: data?.request_id,
          withPictures: !!payload?.withPictures,
        });
      } catch (err) {
        console.error(err);
      }
    },
    [autoTranslate, isAutoTranslateSupported, closeHandler, entityType, entityId, showReportLoadingToast]
  );

  const handleDownloadAll = useCallback(
    (reportOptions, picture, version = REPORT_VERSION) => {
      reportOptions.forEach(option => handleOptionSelected(option, picture, version));
    },
    [handleOptionSelected]
  );

  const filter = useMemo(
    () => ({
      damages: flags?.has_damages !== false,
      repair_budget: !!flags?.has_repair_budget,
      signature: !!flags?.has_signature,
      divergence: !!flags?.has_finished_divergence,
      predial:
        (me?.profile?.franchisee_type !== 'OFFICE' && me?.type !== 'OFFICE') ||
        me?.franchiseeId === 171 ||
        me?.franchiseeId === 12,
    }),
    [flags, me]
  );

  const filteredOptions = useMemo(() => {
    if (!show || !Array.isArray(options)) return [];

    const activePartyDivergences = DIVERGENCE_PARTIES.filter(p => !!flags?.[p.flag]);
    const result = [];

    options.forEach(option => {
      if (filter[option.type] === false) return;

      if (
        option.type === 'inspection' &&
        option.evaluations !== undefined &&
        country !== 'PT' &&
        option.evaluations[0] !== 'Reprovado'
      ) {
        return;
      }

      if (option.type === 'divergence') {
        result.push(option);

        // Only inject party-specific reports when more than 1 party has a finished divergence
        if (activePartyDivergences.length > 1) {
          activePartyDivergences.forEach(partyConfig => {
            result.push({
              ...option,
              label: intl.formatMessage({
                id: partyConfig.labelId,
                defaultMessage: partyConfig.defaultMessage,
              }),
              tags: [partyConfig.party],
            });
          });
        }
        return;
      }

      result.push(option);
    });

    return result;
  }, [show, options, filter, flags, country, intl]);

  const renderOptionsList = () => {
    if (!show) return null;

    if (filteredOptions.length === 0) {
      return <li className={classes.listTitle}>{intl.formatMessage({ id: 'REPORT.NONEXISTENT' })}</li>;
    }

    const photosSection = filteredOptions.map((option, index) => (
      <React.Fragment key={`photos-${option.type}-${option.label}`}>
        {index === 0 && (
          <>
            <li className={classes.listTitle}>
              <div className={classes.reportIcon}>
                <ReportPhotos viewBox="0 0 24 24" />
              </div>
              {intl.formatMessage({ id: 'REPORT.WITH_PICTURES' })}
            </li>
            <li>
              <button
                type="button"
                className={classes.button}
                onClick={() => handleDownloadAll(filteredOptions, true, REPORT_VERSION)}
              >
                <div className={classes.reportIcon}>
                  <ReportItemDownloadAll viewBox="0 0 24 24" />
                  {intl.formatMessage({ id: 'REPORT.DOWNLOADALLPHOTOS' })}
                </div>
                <div className={classes.reportIconDownload}>
                  <ReportItemDownload viewBox="0 0 24 24" />
                </div>
              </button>
            </li>
          </>
        )}
        <FeatureSwitch feature={option.type === 'damages' && option.tags ? 'REPORT_DAMAGE_TAGS' : ''}>
          <li>
            <button
              type="button"
              className={classes.button}
              onClick={() => handleOptionSelected(option, true, REPORT_VERSION)}
              data-cy={`${String(option.label).toLowerCase().replaceAll(' ', '-')}-report-with-pictures-option`}
            >
              <div className={classes.reportIcon}>
                <ReportItem viewBox="0 0 24 24" />
                {option.label}
              </div>
              <div className={classes.reportIconDownload}>
                <ReportItemDownload viewBox="0 0 24 24" />
              </div>
            </button>
          </li>
        </FeatureSwitch>
      </React.Fragment>
    ));

    const printSection = filteredOptions.map((option, index) => (
      <React.Fragment key={`print-${option.type}-${option.label}`}>
        {index === 0 && (
          <>
            <li className={classes.listTitle}>
              <div className={classes.reportIcon}>
                <ReportPrint viewBox="0 0 24 24" />
              </div>
              {intl.formatMessage({ id: 'REPORT.WITHOUT_PICTURES' })}
            </li>
            <li>
              <button
                type="button"
                className={classes.button}
                onClick={() => handleDownloadAll(filteredOptions, false, REPORT_VERSION)}
              >
                <div className={classes.reportIcon}>
                  <ReportItemDownloadAll viewBox="0 0 24 24" />
                  {intl.formatMessage({ id: 'REPORT.DOWNLOADALL' })}
                </div>
                <div className={classes.reportIconDownload}>
                  <ReportItemDownload viewBox="0 0 24 24" />
                </div>
              </button>
            </li>
          </>
        )}
        <FeatureSwitch feature={option.type === 'damages' && option.tags ? 'REPORT_DAMAGE_TAGS' : ''}>
          <li>
            <button
              type="button"
              className={classes.button}
              onClick={() => handleOptionSelected(option, false, REPORT_VERSION)}
              data-cy={`${String(option.label).toLowerCase().replaceAll(' ', '-')}-report-without-pictures-option`}
            >
              <div className={classes.reportIcon}>
                <ReportItem viewBox="0 0 24 24" />
                {option.label}
              </div>
              <div className={classes.reportIconDownload}>
                <ReportItemDownload viewBox="0 0 24 24" />
              </div>
            </button>
          </li>
        </FeatureSwitch>
      </React.Fragment>
    ));

    return (
      <>
        {photosSection}
        {printSection}
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Popper open={show} className={classes.position} anchorEl={anchorEl} placement="bottom-start" transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={closeHandler}>
                <ul className={classes.reportOptions}>
                  {isAutoTranslateSupported && (
                    <li className={classes.languageSelectorRow}>
                      <button
                        type="button"
                        className={`${classes.langButton} ${!autoTranslate ? classes.activeLang : ''}`}
                        onClick={() => setAutoTranslate(false)}
                      >
                        {intl.formatMessage({ id: 'REPORT.LANG_ORIGINAL' })}
                      </button>
                      <button
                        type="button"
                        className={`${classes.langButton} ${autoTranslate ? classes.activeLang : ''}`}
                        onClick={() => setAutoTranslate(true)}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" style={{ marginRight: 6 }}>
                          <defs>
                            <clipPath id="circle-clip-us">
                              <circle cx="12" cy="12" r="12" />
                            </clipPath>
                          </defs>
                          <g clipPath="url(#circle-clip-us)">
                            <rect width="24" height="24" fill="#FFFFFF" />
                            <rect y="0" width="24" height="3" fill="#B22234" />
                            <rect y="6" width="24" height="3" fill="#B22234" />
                            <rect y="12" width="24" height="3" fill="#B22234" />
                            <rect y="18" width="24" height="3" fill="#B22234" />
                            <rect width="11" height="11" fill="#3C3B6E" />
                            <polygon points="5.5,3.5 6,5 4.5,4 6.5,4 5,5" fill="#FFFFFF" />
                          </g>
                        </svg>
                        {intl.formatMessage({ id: 'REPORT.LANG_ENGLISH' })}
                      </button>
                    </li>
                  )}
                  {renderOptionsList()}
                </ul>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

ReportOptions.propTypes = {
  anchorEl: PropTypes.object,
  closeHandler: PropTypes.func.isRequired,
  options: PropTypes.array,
  entityId: PropTypes.string,
  intl: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  flags: PropTypes.object,
};

ReportOptions.defaultProps = {
  anchorEl: undefined,
  entityId: null,
  options: [],
  flags: null,
};

export default injectIntl(ReportOptions);
